import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FileText, Search, Loader2, AlertTriangle, RefreshCcw, Inbox, ChevronDown,
} from "lucide-react";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";

/**
 * OPS-DOC-001 · Operations → Documents workspace (list view).
 *
 * Replaces the "Module under construction" placeholder with a
 * programme-wide, read-only document library:
 *
 *   * Search across title, filename, category, customer name/email,
 *     project reference/address/postcode.
 *   * Active / Archived / All filter driven by the canonical
 *     ``status`` field (`active` vs `deleted`).
 *   * Category filter from the canonical `DOCUMENT_CATEGORIES` list.
 *   * URL-backed `q` / `filter` / `category` state — refresh, direct
 *     link and browser Back all restore the same list state.
 *   * Selecting a row navigates to `/operations/documents/:id`,
 *     carrying the list state in `?return=…` so closing detail restores
 *     the exact previous search + filter.
 *
 * NO writes. NO upload / edit / delete. The existing project-level
 * document workspace is untouched.
 */
const FILTERS = [
    { key: "active",   label: "Active" },
    { key: "archived", label: "Archived" },
    { key: "all",      label: "All" },
];

// OPS-008A · Reference-health classifier presentation.
const REFERENCE_HEALTH_BADGE = {
    healthy:           { label: "Linked",                   cls: "bg-[#EAF6E1] text-[#3E9E2E]" },
    missing_project:   { label: "Missing project",          cls: "bg-[#FEF3C7] text-[#92400E]" },
    missing_customer:  { label: "Missing customer",         cls: "bg-[#FEF3C7] text-[#92400E]" },
    missing_both:      { label: "Missing project & customer", cls: "bg-[#FDECEC] text-[#B91C1C]" },
    legacy_unlinked:   { label: "Legacy — no link",         cls: "bg-[#EEF2FF] text-[#1E3A8A]" },
};

function encodeReturn(params) {
    const usp = new URLSearchParams();
    if (params.q) usp.set("q", params.q);
    if (params.filter && params.filter !== "active") usp.set("filter", params.filter);
    if (params.category) usp.set("category", params.category);
    if (params.reference_health) usp.set("reference_health", params.reference_health);
    const s = usp.toString();
    return s ? `?${s}` : "";
}

function statusBadge(row) {
    if (row.is_archived) return { label: "Archived", cls: "bg-[#F5F5F7] text-[#4B5565]" };
    return { label: "Active", cls: "bg-[#EAF6E1] text-[#3E9E2E]" };
}

function humanDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
}

function humanSize(bytes) {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function OperationsDocuments() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const urlQ = searchParams.get("q") || "";
    const filter = searchParams.get("filter") || "active";
    const category = searchParams.get("category") || "";
    const referenceHealth = searchParams.get("reference_health") || "";

    const [draft, setDraft] = useState(urlQ);
    const lastSyncedUrlQRef = useRef(urlQ);
    useEffect(() => {
        if (urlQ !== lastSyncedUrlQRef.current) {
            lastSyncedUrlQRef.current = urlQ;
            setDraft(urlQ);
        }
    }, [urlQ]);

    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const resp = await api.opsListDocuments({
                q: urlQ, filter, category, reference_health: referenceHealth,
            });
            setRows(resp.documents || []);
            setCategories(resp.categories || []);
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || "Failed to load documents";
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [urlQ, filter, category, referenceHealth]);

    useEffect(() => { load(); }, [load]);

    const setUrl = useCallback((patch) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(patch).forEach(([k, v]) => {
                if (v === "" || v == null || (k === "filter" && v === "active")) next.delete(k);
                else next.set(k, v);
            });
            return next;
        }, { replace: false });
    }, [setSearchParams]);

    const onSubmitSearch = (e) => {
        e.preventDefault();
        setUrl({ q: draft.trim() });
    };

    const openDocument = (row) => {
        navigate(`/operations/documents/${row.id}${encodeReturn({ q: urlQ, filter, category, reference_health: referenceHealth })}`);
    };

    const filtersApplied = urlQ || filter !== "active" || category || referenceHealth;
    const empty = !loading && !err && rows.length === 0 && !filtersApplied;
    const noResults = !loading && !err && rows.length === 0 && filtersApplied;

    const categoryOptions = useMemo(() => (
        [{ value: "", label: "All categories" }].concat(
            categories.map((c) => ({ value: c, label: c })),
        )
    ), [categories]);

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-documents-page">
                <PageHeader
                    eyebrow="OPERATIONS"
                    title="Documents"
                    subtitle="Programme-wide document library: site surveys, contracts, sign-off packs and handover PDFs."
                    icon={FileText}
                    backFallback="/operations"
                    testid="ops-documents"
                />

                {/* Toolbar */}
                <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
                    <form onSubmit={onSubmitSearch} className="flex-1 relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                        <input
                            data-testid="ops-documents-search"
                            className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-[#FAFAFA] focus:bg-white focus:border-[#131B2F] outline-none text-sm"
                            placeholder="Search title, filename, customer, project reference or postcode…"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                        />
                    </form>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative">
                            <select
                                data-testid="ops-documents-category"
                                value={category}
                                onChange={(e) => setUrl({ category: e.target.value })}
                                className="h-8 pl-3 pr-8 rounded-full text-xs font-medium border bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7] appearance-none cursor-pointer"
                            >
                                {categoryOptions.map((opt) => (
                                    <option key={opt.value || "__all__"} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#4B5565]" />
                        </div>
                        <button
                            type="button"
                            data-testid="ops-documents-filter-needs-review"
                            onClick={() => setUrl({ reference_health: referenceHealth === "needs_review" ? "" : "needs_review" })}
                            className={
                                "h-8 px-3 rounded-full text-xs font-medium border transition-colors " +
                                (referenceHealth === "needs_review"
                                    ? "bg-[#B45309] text-white border-[#B45309]"
                                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]")
                            }
                            aria-pressed={referenceHealth === "needs_review"}
                            title="Show documents whose project or customer link is missing"
                        >
                            Needs review
                        </button>
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                data-testid={`ops-documents-filter-${f.key}`}
                                onClick={() => setUrl({ filter: f.key })}
                                className={
                                    "h-8 px-3 rounded-full text-xs font-medium border transition-colors " +
                                    (filter === f.key
                                        ? "bg-[#131B2F] text-white border-[#131B2F]"
                                        : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]")
                                }
                                aria-pressed={filter === f.key}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="mt-4 rounded-xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="ops-documents-list">
                    {loading && (
                        <div className="p-10 flex items-center justify-center text-sm text-[#4B5565]" data-testid="ops-documents-loading">
                            <Loader2 className="animate-spin mr-2" size={16} /> Loading documents…
                        </div>
                    )}
                    {!loading && err && (
                        <div className="p-10 text-center" data-testid="ops-documents-error">
                            <AlertTriangle size={22} className="mx-auto text-[#B91C1C]" />
                            <div className="mt-3 text-sm text-[#131B2F]">{err}</div>
                            <button
                                onClick={load}
                                className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid="ops-documents-retry"
                            >
                                <RefreshCcw size={14} /> Try again
                            </button>
                        </div>
                    )}
                    {!loading && !err && empty && (
                        <div className="p-12 text-center" data-testid="ops-documents-empty">
                            <Inbox size={22} className="mx-auto text-[#9CA3AF]" />
                            <div className="mt-3 text-sm text-[#131B2F]">No documents yet.</div>
                            <div className="mt-1 text-xs text-[#4B5565]">Documents appear here once a project uploads one.</div>
                        </div>
                    )}
                    {!loading && !err && noResults && (
                        <div className="p-12 text-center" data-testid="ops-documents-no-results">
                            <Search size={22} className="mx-auto text-[#9CA3AF]" />
                            <div className="mt-3 text-sm text-[#131B2F]">No matches for the current search or filters.</div>
                            <button
                                onClick={() => { setDraft(""); setUrl({ q: "", filter: "active", category: "" }); }}
                                className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid="ops-documents-clear"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                    {!loading && !err && rows.length > 0 && (
                        <ul className="divide-y divide-[#EDEFF3]">
                            {rows.map((row) => {
                                const badge = statusBadge(row);
                                const health = REFERENCE_HEALTH_BADGE[row.reference_health] || null;
                                // OPS-008A · Never show "Unknown" for a broken link.
                                // Use the explicit reference-health state so
                                // operators can distinguish "no data yet" from
                                // "the linked entity has been removed".
                                const customerLabel = row.customer_exists
                                    ? (row.customer_name || row.customer_email || "—")
                                    : (row.reference_health === "legacy_unlinked"
                                        ? "No linked customer"
                                        : "Customer no longer available");
                                const projectLabel = row.project_exists
                                    ? (row.project_reference || "—")
                                    : (row.reference_health === "legacy_unlinked"
                                        ? "No linked project"
                                        : "Project no longer available");
                                return (
                                    <li
                                        key={row.id}
                                        data-testid={`ops-document-row-${row.id}`}
                                        data-reference-health={row.reference_health || ""}
                                        className="p-4 md:p-5 hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                                        onClick={() => openDocument(row)}
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center md:gap-6">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="text-sm md:text-base font-medium text-[#131B2F] truncate">
                                                        {row.title || row.file_name || "Untitled document"}
                                                    </div>
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                                                    {health && row.reference_health !== "healthy" && (
                                                        <span
                                                            data-testid={`ops-document-refhealth-${row.id}`}
                                                            className={`text-[10px] px-2 py-0.5 rounded-full ${health.cls}`}
                                                        >
                                                            {health.label}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-1 text-xs text-[#4B5565] truncate">
                                                    <span className="font-medium text-[#131B2F]">{row.category || "Uncategorised"}</span>
                                                    {" · "}{customerLabel}
                                                    {" · "}{projectLabel}
                                                </div>
                                            </div>
                                            <div className="mt-3 md:mt-0 flex items-center gap-4 md:gap-6 text-xs text-[#131B2F]">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] uppercase tracking-wide text-[#4B5565]">Uploaded</span>
                                                    <span className="font-medium">{humanDate(row.uploaded_at)}</span>
                                                </div>
                                                <div className="hidden md:flex flex-col">
                                                    <span className="text-[10px] uppercase tracking-wide text-[#4B5565]">Size</span>
                                                    <span className="font-medium">{humanSize(row.file_size) || "—"}</span>
                                                </div>
                                                <Link
                                                    to={`/operations/documents/${row.id}${encodeReturn({ q: urlQ, filter, category, reference_health: referenceHealth })}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="hidden md:inline-block text-xs text-[#3E9E2E] hover:underline"
                                                    data-testid={`ops-document-open-${row.id}`}
                                                >
                                                    Open →
                                                </Link>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </PageContainer>
        </OperationsShell>
    );
}
